import { useState } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Mail, Loader2, ArrowRight, Sparkles, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Không thể gửi email khôi phục.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-10rem)] px-4">
      <Helmet>
        <title>Quên mật khẩu | Flashcard Platform</title>
      </Helmet>

      <div className="w-full max-w-md relative">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 blur-[100px] rounded-full -z-10" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-violet-500/20 blur-[100px] rounded-full -z-10" />
        
        <Card className="border-2 rounded-[3rem] shadow-2xl overflow-hidden bg-background/80 backdrop-blur-xl">
          <CardHeader className="space-y-4 pt-12 pb-8">
            <div className="mx-auto w-16 h-16 rounded-3xl bg-primary flex items-center justify-center shadow-xl shadow-primary/20 mb-2">
              <Sparkles className="h-8 w-8 text-white fill-current" />
            </div>
            <CardTitle className="text-3xl font-black text-center tracking-tight">Quên mật khẩu?</CardTitle>
            <CardDescription className="text-center text-lg font-medium">
              Nhập email để nhận liên kết khôi phục mật khẩu.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 px-10 pb-10">
            {success ? (
              <div className="bg-primary/10 border-2 border-primary/20 p-6 rounded-3xl text-center space-y-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold">Kiểm tra hộp thư!</h3>
                <p className="text-muted-foreground font-medium">
                  Chúng tôi đã gửi hướng dẫn đổi lại mật khẩu đến <strong>{email}</strong>.
                </p>
                <Button variant="outline" className="w-full rounded-2xl font-bold" onClick={() => setSuccess(false)}>
                  Thử lại với email khác
                </Button>
              </div>
            ) : (
              <form onSubmit={handleResetRequest} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-bold text-xs uppercase tracking-widest text-muted-foreground ml-1">Email của bạn</Label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="name@example.com" 
                      className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required 
                    />
                  </div>
                </div>
                {error && <p className="text-sm font-bold text-destructive px-1">{error}</p>}
                <Button type="submit" className="w-full h-14 rounded-2xl text-lg font-bold shadow-xl shadow-primary/20 group" disabled={loading}>
                  {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                  Gửi yêu cầu <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="flex flex-wrap items-center justify-center gap-1.5 pb-12 text-base font-medium text-muted-foreground border-t-2 border-dashed border-muted-foreground/10 pt-8 mt-2">
            <Link to="/login" className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-black">
              <ArrowLeft className="h-4 w-4" /> Quay lại đăng nhập
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
